import { MdClose } from 'react-icons/md';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: ReactNode;
  /** Ligne de pastilles sous le titre (volume, motifs…). */
  meta?: ReactNode;
  /** En-tête libre : remplace le titre, la description et les pastilles (le titre reste le nom accessible). */
  header?: ReactNode;
  children?: ReactNode;
  className?: string;
};

/** Panneau qui glisse depuis la droite, par-dessus la page. Échap ou clic dehors le ferme. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  meta,
  header,
  children,
  className,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Dernière version de `onClose` sans relancer l'effet (voir `Modal`).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 bg-brand-900/40 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex h-full w-full max-w-md flex-col bg-surface shadow-card outline-none',
          className,
        )}
      >
        <div className="relative z-30 shrink-0 border-b border-border-subtle">
          {header ? (
            <>
              {title ? (
                <h2 id={titleId} className="sr-only">
                  {title}
                </h2>
              ) : null}
              <div className="px-6 py-4 pr-14">{header}</div>
            </>
          ) : (
            <div className="flex flex-col gap-1 px-6 py-5 pr-14">
              {title ? (
                <h2 id={titleId} className="truncate text-lg font-semibold text-content">
                  {title}
                </h2>
              ) : null}
              {description ? <p className="text-sm text-content-muted">{description}</p> : null}
              {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute right-4 top-4 rounded-lg p-1.5 text-content-subtle transition-colors hover:bg-surface-muted hover:text-content"
          >
            <MdClose className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-surface-muted px-6 py-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
